/**
 * Cài đặt máy in hoá đơn của thiết bị này + tờ "Máy in" mở từ nút trên hoá đơn. Xem `may-in.ts` để biết ba cách in
 * và vì sao máy in WiFi đi qua hộp in của hệ điều hành.
 */
import { Bluetooth, Check, Printer, Usb } from 'lucide-react'
import { useState } from 'react'
import {
  type CaiDatMayIn,
  chonMayInUsb,
  coBluetooth,
  coUsb,
  docCaiDatMayIn,
  inThu,
  type KieuMayIn,
  LoiMayIn,
  luuCaiDatMayIn,
  quetMayInBluetooth,
} from './may-in.ts'
import { IN } from './text/in-hoa-don.ts'
import { T } from './text/vi.ts'
import { ThongBao, To } from './ui.tsx'

function cauLoi(e: unknown): string {
  if (e instanceof LoiMayIn) {
    if (e.loai === 'khong-ho-tro') return IN.khongHoTroBluetooth
    if (e.loai === 'huy') return IN.huyChon
    if (e.loai === 'khong-mo-duoc') return IN.khongTimThayCong
    return IN.khongGhiDuoc
  }
  return IN.khongGhiDuoc
}

const KIEU: { ma: KieuMayIn; ten: string; mo: string }[] = [
  { ma: 'trinh-duyet', ten: IN.kieuTrinhDuyet, mo: IN.kieuTrinhDuyetMoTa },
  { ma: 'usb', ten: IN.kieuUsb, mo: IN.kieuUsbMoTa },
  { ma: 'bluetooth', ten: IN.kieuBluetooth, mo: IN.kieuBluetoothMoTa },
]

export function CaiDatMayInPanel({ tenTiem }: { tenTiem: string }) {
  const [c, setC] = useState<CaiDatMayIn>(docCaiDatMayIn)
  const [bao, setBao] = useState<{ loai: 'thanh-cong' | 'loi'; chu: string } | null>(null)
  const [dang, setDang] = useState(false)

  function doi(sua: Partial<CaiDatMayIn>) {
    const moi = { ...c, ...sua }
    setC(moi)
    luuCaiDatMayIn(moi)
    setBao(null)
  }

  async function noi() {
    setDang(true)
    setBao(null)
    try {
      if (c.kieu === 'bluetooth') {
        const r = await quetMayInBluetooth()
        doi({ tenThietBi: r.ten, idThietBi: r.id })
      } else if (c.kieu === 'usb') {
        const r = await chonMayInUsb()
        doi({ tenThietBi: r.ten, idThietBi: null })
      }
      setBao({ loai: 'thanh-cong', chu: IN.noiXong })
    } catch (e) {
      setBao({ loai: 'loi', chu: cauLoi(e) })
    } finally {
      setDang(false)
    }
  }

  async function thu() {
    setDang(true)
    setBao(null)
    try {
      await inThu(tenTiem, c)
      setBao({ loai: 'thanh-cong', chu: IN.daIn })
    } catch (e) {
      setBao({ loai: 'loi', chu: cauLoi(e) })
    } finally {
      setDang(false)
    }
  }

  const hoTro =
    c.kieu === 'trinh-duyet' ||
    (c.kieu === 'usb' && coUsb()) ||
    (c.kieu === 'bluetooth' && coBluetooth())
  const canNoi = c.kieu !== 'trinh-duyet'

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <p className="chu-phu chu-16">{IN.giaiThichMayIn}</p>
      <div className="truong">
        <span className="nhan">{IN.cachIn}</span>
        <div className="hang-chip">
          {KIEU.map((k) => (
            <button
              key={k.ma}
              type="button"
              aria-pressed={c.kieu === k.ma}
              className={`chip ${c.kieu === k.ma ? 'dang-chon' : ''}`}
              onClick={() => doi({ kieu: k.ma, tenThietBi: null, idThietBi: null })}
            >
              {c.kieu === k.ma && <Check size={16} />}
              {k.ma === 'usb' && <Usb size={16} />}
              {k.ma === 'bluetooth' && <Bluetooth size={16} />}
              {k.ten}
            </button>
          ))}
        </div>
        <p className="chu-phu chu-16">{KIEU.find((k) => k.ma === c.kieu)?.mo}</p>
      </div>

      <div className="truong">
        <span className="nhan">{IN.khoGiay}</span>
        <div className="hang-chip">
          {([58, 80] as const).map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={c.kho === k}
              className={`chip ${c.kho === k ? 'dang-chon' : ''}`}
              onClick={() => doi({ kho: k })}
            >
              {c.kho === k && <Check size={16} />}
              {k === 58 ? IN.kho58 : IN.kho80}
            </button>
          ))}
        </div>
      </div>

      {!hoTro && (
        <ThongBao loai="canh-bao">
          {c.kieu === 'usb' ? IN.khongHoTroUsb : IN.khongHoTroBluetooth}
        </ThongBao>
      )}

      {canNoi && hoTro && (
        <div className="truong">
          <p className="chu-phu chu-16">
            {c.tenThietBi === null ? IN.chuaNoi : IN.thietBiDaNoi(c.tenThietBi)}
          </p>
          <button
            type="button"
            className={`nut nut-lon ${c.tenThietBi === null ? 'nut-chinh' : 'nut-vien'}`}
            disabled={dang}
            onClick={() => void noi()}
          >
            {dang
              ? IN.dangNoi
              : c.tenThietBi !== null
                ? IN.doiThietBi
                : c.kieu === 'bluetooth'
                  ? IN.quetBluetooth
                  : IN.noiUsb}
          </button>
        </div>
      )}

      {bao !== null && <ThongBao loai={bao.loai}>{bao.chu}</ThongBao>}

      <button
        type="button"
        className={`nut nut-lon ${canNoi && c.tenThietBi === null ? 'nut-vien' : 'nut-chinh'}`}
        disabled={dang || !hoTro || (canNoi && c.tenThietBi === null && c.idThietBi === null)}
        onClick={() => void thu()}
      >
        <Printer size={18} />
        {IN.inThu}
      </button>
    </div>
  )
}

/** Tờ "Máy in" mở từ hoá đơn — mọi người thu tiền cài được máy in của quầy mình đang đứng. */
export function ToMayIn({ tenTiem, onDong }: { tenTiem: string; onDong: () => void }) {
  return (
    <To nhan={IN.tieuDeMayIn} onDong={onDong}>
      <header className="to-dau">
        <h2>{IN.tieuDeMayIn}</h2>
        <button type="button" className="nut nut-nhat" aria-label={T.close} onClick={onDong}>
          {T.close}
        </button>
      </header>
      <div className="to-than">
        <CaiDatMayInPanel tenTiem={tenTiem} />
      </div>
    </To>
  )
}
